import React, { useState, useEffect, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import { EditOutlined, DeleteOutlined, Add } from "@mui/icons-material"
import { Button, IconButton, Typography, Box, CircularProgress } from "@mui/material"
import { useNavigate } from "react-router-dom"
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import { hasPermission, PERMISSIONS } from "../../utils/permissions"
import { useAuth } from "../../context/AuthContext"

const SensorTypeList = () => {
  const navigate = useNavigate()
  const [sensorTypes, setSensorTypes] = useState([])
  const { user } = useAuth();  // Get the whole user object
  const userPermissions = user?.permissions;
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)
  const [totalItems, setTotalItems] = useState(0)

  const fetchSensorTypes = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.get(API_ENDPOINTS.SENSOR_TYPE.GET_ALL, {
        params: { page: page + 1, limit: pageSize },
      })
      setSensorTypes(response.data.sensorTypes)
      setTotalItems(response.data.totalItems)
      setLoading(false)
    } catch (err) {
      console.error("Error fetching sensor types:", err)
      setError("Failed to fetch sensor types. Please try again.")
      setLoading(false)
    }
  }, [page, pageSize])

  useEffect(() => {
    fetchSensorTypes()
  }, [fetchSensorTypes])

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this sensor type?")) {
      try {
        await api.delete(API_ENDPOINTS.SENSOR_TYPE.DELETE(id))
        fetchSensorTypes()
      } catch (err) {
        console.error("Error deleting sensor type:", err)
        setError("Failed to delete sensor type. Please try again.")
      }
    }
  }

  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    ...(hasPermission(userPermissions, 'sensortype', PERMISSIONS.UPDATE) || hasPermission(userPermissions, 'sensortype', PERMISSIONS.DELETE) ? [{
      field: "actions",
      headerName: "Actions", 
      flex: 0.3,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {hasPermission(userPermissions, 'sensortype', PERMISSIONS.UPDATE) && (
            <IconButton
              size="small"
              sx={{ color: "#6C757D" }}
              onClick={() => navigate(`/sensor-types/edit/${params.row.id}`)}
            >
              <EditOutlined fontSize="inherit" />
            </IconButton>
          )}
          {hasPermission(userPermissions, 'sensortype', PERMISSIONS.DELETE) && (
            <IconButton size="small" sx={{ color: "#E31B54" }} onClick={() => handleDelete(params.row.id)}>
              <DeleteOutlined fontSize="inherit" />
            </IconButton>
          )}
        </Box>
      ),
    }] : [])
  ]

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", pr: 2, pt: 0, backgroundColor: "background.paper", borderRadius: 1 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        Sensor Types
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {hasPermission(userPermissions, 'sensortype', PERMISSIONS.CREATE) && (
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => navigate("/sensor-types/create")}
            sx={{
            textTransform: "none",
            backgroundColor: "#1967D2",
            color: "#FFFFFF",
            minWidth: "120px",
            px: 2,
            height: "32px",
            borderRadius: "16px",
            "&:hover": {
              backgroundColor: "#1756B6",
            },
          }}
        >
          New sensor type
          </Button>
        )}
      </Box>
      <DataGrid
        rows={sensorTypes}
        columns={columns}
        pagination
        rowCount={totalItems}
        pageSizeOptions={[5, 10, 25]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(newModel) => {
          setPage(newModel.page)
          setPageSize(newModel.pageSize)
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        sx={{
          borderRadius: 6,
          borderColor: "background.primary",
          boxShadow: 1,
          color: "text.secondary",
          bgcolor: 'background.paper',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            }
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderColor: 'background.default',
          },
        }}
      />
    </Box>
  )
}

export default SensorTypeList
